import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, Trash2, FileText, ClipboardList, Mic, Mail, CalendarDays, Pencil, ListTodo, GitBranch, Sparkles, Brain, Gauge, Tags, BookOpen, Layers, Map, Lightbulb, IdCard, Drama, Eye , Users } from "lucide-react";
import FollowupEmailPanel from "../FollowupEmailPanel";
import { SessionTranscriptTab } from "./SessionTranscriptTab";
import { SessionNoteTab, NOTE_FIELDS } from "./SessionNoteTab";
import { AnalysisTab, hasAnyAnalysis } from "./analyses/AnalysisTab";
import { ageFromDob, isAdoAge } from "../../lib/age";
import { GenerateReportFromTranscript } from "../GenerateReportFromTranscript";
import { PerfectionismScanCard } from "./PerfectionismScanCard";
import { API } from "../../App";
import { formatDateSafe } from "../../lib/formatDate";

const fmtDay = (d) => formatDateSafe(d, { day: "numeric", month: "long", year: "numeric" });

const TABS = [
  { id: "summary", label: "Résumé", icon: FileText },
  { id: "homework", label: "Travail personnel", icon: ListTodo },
  { id: "next_plan", label: "Plan prochaine séance", icon: CalendarDays },
  { id: "transcript", label: "Transcription", icon: Mic },
  { id: "report", label: "Compte rendu", icon: ClipboardList },
  { id: "email", label: "E-mail de suivi", icon: Mail },
];

const TCC_TABS = [
  { id: "brief", label: "Note préparatoire", icon: BookOpen },
  { id: "abc", label: "Analyse ABC", icon: GitBranch },
  { id: "abc_adaptive", label: "ABC adaptatif", icon: Sparkles },
  { id: "restructuring", label: "Restructuration cognitive", icon: Brain },
  { id: "labels", label: "Étiquettes TCC", icon: Tags },
  { id: "cts_r", label: "Détails TCC (CTS-R)", icon: Gauge },
];

const ST_TABS = [
  { id: "st_abc", label: "ABC schémas", icon: Layers },
  { id: "mode_map", label: "Carte des modes", icon: Map },
  { id: "mode_map_hypothesis", label: "Hypothèses", icon: Lightbulb },
  { id: "flashcard", label: "Fiche mémo", icon: IdCard },
  { id: "experiential", label: "Travail expérientiel", icon: Drama },
  { id: "st_labels", label: "Étiquetage ST", icon: Tags },
  { id: "st_reflection", label: "Auto-réflexion", icon: Eye },
];

const ADO_TABS = [
  { id: "ado_sorc", label: "Analyse fonctionnelle ado", icon: Layers },
  { id: "parent_guidance", label: "Guidance parentale", icon: Users },
];

export const TAB_GROUPS = [
  { id: "general", label: "Général", tabs: TABS },
  { id: "tcc", label: "TCC", tabs: TCC_TABS },
  { id: "st", label: "Schémas", tabs: ST_TABS },
  { id: "ado", label: "Ados 12-17", tabs: ADO_TABS },
];

const ANALYSIS_KEYS = [...TCC_TABS, ...ST_TABS, ...ADO_TABS].map((t) => t.id);

const dayName = (d) => { try { return new Date(d).toLocaleDateString("fr-FR", { weekday: "long" }); } catch { return ""; } };

export const SessionCarousel = ({ sessions, index, onChange }) => {
  const s = sessions[index];
  if (!s) return null;
  const nr = sessions.length - index;
  return (
    <div className="flex items-center gap-3" data-testid="session-carousel">
      <Button variant="outline" size="icon" className="rounded-full shrink-0" onClick={() => onChange(index + 1)} disabled={index >= sessions.length - 1} data-testid="carousel-prev" title="Séance précédente"><ChevronLeft className="w-4 h-4" /></Button>
      <div className="flex-1 rounded-xl border-2 border-primary/60 bg-primary/5 px-4 py-3 flex items-center gap-3 flex-wrap" data-testid="carousel-card">
        <span className="font-semibold text-primary">Nr {nr}</span>
        <span className="text-muted-foreground">|</span>
        <span className={`text-xs px-2 py-0.5 rounded-full ${s.session_type === "initial" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" : s.session_type === "therapie_couple" ? "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300" : s.session_type === "suivi_tcc_ado" ? "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"}`}>{s.session_type === "initial" ? "Entretien initial" : s.session_type === "therapie_couple" ? "Thérapie de couple" : s.session_type === "suivi_tcc_ado" ? "Suivi ados 12-17" : "Séance de suivi"}</span>
        {s.source === "calendly" && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Calendly</span>}
        {s.co_patient_ids?.length > 0 && s.patient_name && <span className="text-xs text-rose-700 dark:text-rose-300" data-testid="carousel-couple-names">{s.patient_name}</span>}
        <span className="text-muted-foreground">|</span>
        <span className="text-sm">{fmtDay(s.session_date)}</span>
        <span className="text-sm capitalize text-muted-foreground">{dayName(s.session_date)}</span>
        <span className="ml-auto text-xs text-muted-foreground">{index + 1} / {sessions.length}</span>
      </div>
      <Button variant="outline" size="icon" className="rounded-full shrink-0" onClick={() => onChange(index - 1)} disabled={index <= 0} data-testid="carousel-next" title="Séance suivante"><ChevronRight className="w-4 h-4" /></Button>
    </div>
  );
};

export const SessionDetail = ({ report, patient, user, onUpdated, onDeleted }) => {
  const [group, setGroup] = useState("general");
  const [tab, setTab] = useState("summary");
  const [busy, setBusy] = useState(false);
  const analysisKeys = ANALYSIS_KEYS;
  const [participants, setParticipants] = useState([]);
  const [target, setTarget] = useState("");
  const coupleKey = (report.co_patient_ids || []).join(",");
  useEffect(() => {
    if (!coupleKey) { setParticipants([]); setTarget(""); return; }
    axios.get(`${API}/session-reports/${report.report_id}/analyses/participants`, { withCredentials: true })
      .then((r) => { const ps = r.data.participants || []; setParticipants(ps); setTarget(ps.some((p) => p.user_id === patient?.user_id) ? patient.user_id : (ps[0]?.user_id || "")); })
      .catch(() => setParticipants([]));
  }, [report.report_id, coupleKey, patient?.user_id]);
  const switchGroup = (g) => { setGroup(g); setTab(TAB_GROUPS.find((x) => x.id === g).tabs[0].id); };

  const setType = async (session_type) => {
    try {
      const r = await axios.put(`${API}/session-reports/${report.report_id}`, { session_type }, { withCredentials: true });
      onUpdated(r.data); toast.success("Type de séance mis à jour");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async () => {
    if (!window.confirm("Supprimer définitivement cette séance (compte rendu, transcription, e-mail) ?")) return;
    setBusy(true);
    try { await axios.delete(`${API}/session-reports/${report.report_id}`, { withCredentials: true }); onDeleted(report.report_id); toast.success("Séance supprimée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const patientAge = ageFromDob(patient?.date_of_birth);
  const visibleGroups = TAB_GROUPS.filter((g) => g.id !== "ado" || report.session_type === "suivi_tcc_ado" || g.tabs.some((t) => hasAnyAnalysis(report, t.id)));
  const filled = Object.entries(report.fields || {}).filter(([, v]) => v && String(v).replace(/<[^>]+>/g, "").trim());

  return (
    <div className="rounded-xl border" data-testid={`session-detail-${report.report_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-2 border-b bg-muted/30">
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Type :</span>
          <select className="px-2 py-1 text-sm border rounded-lg bg-background border-border" value={report.session_type} onChange={(e) => setType(e.target.value)} data-testid="session-type-select">
            <option value="initial">Entretien initial</option>
            <option value="suivi_tcc">Séance de suivi TCC</option>
            <option value="therapie_couple">Séance de thérapie de couple</option>
            <option value="suivi_tcc_ado">Suivi TCC enfants & ados (12-17 ans)</option>
          </select>
          {report.session_type === "suivi_tcc_ado" && patientAge !== null && !isAdoAge(patientAge) && (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" data-testid="ado-age-warning">Âge du patient : {patientAge} ans (hors 12-17)</span>
          )}
          {report.session_type !== "suivi_tcc_ado" && report.session_type !== "initial" && isAdoAge(patientAge) && (
            <button type="button" onClick={() => setType("suivi_tcc_ado")} className="text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200 hover:bg-sky-200" data-testid="ado-age-suggest">Patient de {patientAge} ans → passer en suivi ados ?</button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Link to={`/therapist/session-reports?edit=${report.report_id}`}><Button variant="ghost" size="sm" className="gap-1.5" data-testid="session-edit-cr-btn"><Pencil className="w-3.5 h-3.5" /> Éditer le CR</Button></Link>
          <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={remove} disabled={busy} data-testid="session-delete-btn"><Trash2 className="w-3.5 h-3.5" /> Supprimer</Button>
        </div>
      </div>
      <PerfectionismScanCard report={report} participants={participants} onUpdated={onUpdated} />
      <div className="flex items-center gap-1 overflow-x-auto px-2 pt-2 border-b" data-testid="session-tabs">
        <div className="flex items-center rounded-lg bg-muted p-0.5 mr-2 shrink-0 mb-1" data-testid="session-tab-groups">
          {visibleGroups.map((g) => (
            <button key={g.id} onClick={() => switchGroup(g.id)} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${group === g.id ? "bg-background shadow text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid={`session-group-${g.id}`}>
              {g.label}
              {g.id !== "general" && g.tabs.some((t) => hasAnyAnalysis(report, t.id)) && <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 align-middle" />}
            </button>
          ))}
        </div>
        {TAB_GROUPS.find((g) => g.id === group).tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-lg whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? "border-primary text-primary font-medium bg-primary/5" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`session-tab-${t.id}`}>
            <t.icon className="w-4 h-4" /> {t.label}
            {t.id in NOTE_FIELDS && (report[t.id] || Object.values(report.notes_by_patient || {}).some((n) => n?.[t.id])) && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
            {t.id === "transcript" && report.transcript && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
            {analysisKeys.includes(t.id) && hasAnyAnalysis(report, t.id) && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />}
          </button>
        ))}
      </div>
      <div className="p-4">
        {analysisKeys.includes(tab) && <AnalysisTab key={`${report.report_id}-${tab}`} report={report} patient={patient} analysisKey={tab} onUpdated={onUpdated} participants={participants} target={target} onTargetChange={setTarget} />}
        {tab in NOTE_FIELDS && <SessionNoteTab key={`${report.report_id}-${tab}`} report={report} patient={patient} field={tab} onUpdated={onUpdated} participants={participants} target={target} onTargetChange={setTarget} />}
        {tab === "transcript" && <SessionTranscriptTab key={report.report_id} report={report} onUpdated={onUpdated} />}
        {tab === "report" && (
          <div className="space-y-4" data-testid="session-report-fields">
            <div className="flex items-center justify-between gap-2 flex-wrap rounded-lg border bg-muted/20 px-3 py-2">
              <p className="text-xs text-muted-foreground">
                {report.transcript ? "Compte rendu structuré généré par l'IA à partir de la transcription de la séance." : "Ajoutez d'abord une transcription (onglet Transcription) pour générer le compte rendu."}
                {report.fields_generated_at && <span className="block" data-testid="report-fields-meta">Dernière génération : {formatDateSafe(report.fields_generated_at, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}{report.fields_model ? ` · ${report.fields_model}` : ""}</span>}
              </p>
              <GenerateReportFromTranscript reportId={report.report_id} transcript={report.transcript} hasFields={filled.length > 0} variant={filled.length ? "outline" : "default"}
                onGenerated={async () => { const r = await axios.get(`${API}/session-reports/detail/${report.report_id}`, { withCredentials: true }); onUpdated(r.data); }} />
            </div>
            {filled.length === 0 ? <p className="text-sm text-muted-foreground text-center py-6">Aucun champ rempli. <Link className="underline" to={`/therapist/session-reports?edit=${report.report_id}`}>Éditer le compte rendu</Link>.</p>
              : filled.map(([k, v]) => (
                <div key={k}><h4 className="text-sm font-medium text-primary mb-1">{k.replace(/_/g, " ")}</h4><div className="text-sm text-muted-foreground prose prose-sm dark:prose-invert max-w-none" dangerouslySetInnerHTML={{ __html: v }} /></div>
              ))}
          </div>
        )}
        {tab === "email" && <FollowupEmailPanel key={report.report_id} report={report} patient={patient} user={user} onUpdated={onUpdated} />}
      </div>
    </div>
  );
};
